import React, {useState, useRef} from 'react';
import styled from '@emotion/styled';
import SvgBarGraph from '../svgs/SvgBarGraph';
import { PricingComparisonOneYearSummary } from '../models/VmCloudPricing';
import yellowWarningIcon from '../svgs/exclamation-circle.svg';
import envelope from '../svgs/envelope.svg';
import LOLoadingIcon from '../svgs/LOLoadingIcon';

const Title = styled.div`
  font-size: 18px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 700;
  color: #0076CE;
`;

const WrapperTile = styled.div`

`;

const WrapperTileLoading = styled.div`
  text-align: center;
  max-height: fit-content;
  height: fit-content;
  margin-top: 11.5px;
  background: #fff;
  flex: 1;
  @media (max-width: 1300px) {
    max-width: 100%;
  }
`;

const CloudProviderRow = styled.div`
  display: flex;
  padding: 10px 0px 0px 0px;
  flex-direction: row;
  margin-bottom: 25px;
`;

const CloudProviderLastRow = styled.div`
  display: flex;
  padding: 10px 0px 0px 0px;
  flex-direction: row;
`;

const ProviderNameWrapper = styled.div`
  position: relative;
  flex: 0.25;
  text-align: left;
`;

const ColumnGraph = styled.div`
  flex: 2;
  text-align: left;
`;

const Column = styled.div`
  flex: 0.25;
`;

const PriceStyle = styled.div`
  font-size: 18px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 600;
  color: #566072;
  text-align: left;
  max-width: 100px;
  white-space: nowrap;
`;

const Description = styled.div`
  padding-top: 23px;
  height: 100px;
  font-size: 16px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 500;
  color: #566072;
  text-align: left;
`;

const ProviderName = styled.div`
  font-size: 18px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 500;
  color: #566072;
  white-space: nowrap
`;

const GetInTouchWrapper = styled.div`

`;

const GetInTouchIcon = styled.img`
  
`;

const GetInTouchMessage = styled.span`
  font-size: 20px;
  padding-left: 20px;
  padding-right: 20px;
  max-width: 70%;
  color: #566072;
  @media (max-width: 500px) {
    display: block;
    margin-bottom: 15px;
  }
`;

const GetInTouchButton = styled.button`
  
`;

export interface Props {
    data: PricingComparisonOneYearSummary;
    isLoadingPricingComparison: boolean;
    reCaptchaRef: any;
}

/**
 * VmPricingProvider is responsibile for calling our API service to retrieive the backend data, parse it, and render the results. 
 */
export const VmPricingComparisonChart = (props: Props) => {
    const [ showGetInTouchModal, setShowGetInTouchModal] = useState<boolean>();  
    const [ showGetInTouchSuccess, setShowGetInTouchSuccess] = useState<boolean>();
    let loadingIcon = (<LOLoadingIcon />);

    /**
     * Takes in a number and returns the percentage representation for that number.
     * @param val The number you want to get a width percentage value for.
     * @returns The width percentage representation for the given number.
     */
    const GetWidthPctg = (val: number) => {
        let oneYearSumsArray = [
          props.data.awsOneYearSum,
          props.data.azureOneYearSum,
          props.data.googleOneYearSum
        ];
    
        let maxOneYearSum = Math.max.apply(null, oneYearSumsArray);

        return (val * 100) / maxOneYearSum;
    };

    return (
      <>
        {
          props.isLoadingPricingComparison ? 
            (<WrapperTileLoading className="pricing-comparison-chart-wrapper-loading">
              <div>
               {loadingIcon}
              </div>
              <div>
                <p>Processing ...</p>
              </div>
            </WrapperTileLoading>) :
            (<WrapperTile className="pricing-comparison-chart-wrapper">
              <Title>Monthly Public Cloud Pricing Estimate Comparison - {props.data.region}</Title>
              <Description>
                All running VMs have been priced across all three public cloud providers. This is the comparative monthly cost at a 1 year commitment pricing discount.
              </Description>
              <CloudProviderRow className='provider-comparison-row'>
                <ProviderNameWrapper className='provider-name-wrap'>
                  <ProviderName>AWS</ProviderName>
                </ProviderNameWrapper>
                <Column className='provider-price-wrap'>
                  <PriceStyle>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.trunc(props.data.awsOneYearSum))}</PriceStyle>
                </Column>
                <ColumnGraph className='provider-bar-wrap'>
                  {
                    props.data.awsOneYearSum > 0 ?
                    <SvgBarGraph width={GetWidthPctg(props.data.awsOneYearSum)} color={'#FB9803'} />
                    : null
                  }
                  
                  {
                    props.data.awsUnmatchedVmCount > 0 && props.data.awsOneYearSum > 0 ? 
                    <p className="bar-warning"><img src={yellowWarningIcon} width="17" height="17" /> 1 year pricing is unavailable for {props.data.awsUnmatchedVmCount} Vms for the AWS service provider.</p>
                      : null
                  }

                  {
                    props.data.awsUnmatchedVmCount > 0 && props.data.awsOneYearSum === 0 ? 
                    <p className="bar-warning"><img src={yellowWarningIcon} width="17" height="17" /> No pricing Available for this vendor.</p>
                      : null
                  }
                </ColumnGraph>
              </CloudProviderRow>
              <CloudProviderRow className='provider-comparison-row'>
                <ProviderNameWrapper className='provider-name-wrap'>
                  <ProviderName>Azure</ProviderName>
                </ProviderNameWrapper>
                <Column className='provider-price-wrap'>
                  <PriceStyle>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.trunc(props.data.azureOneYearSum))}</PriceStyle>
                </Column>
                <ColumnGraph className='provider-bar-wrap'>
                  {
                    props.data.azureOneYearSum > 0 ?
                    <SvgBarGraph width={GetWidthPctg(props.data.azureOneYearSum)} color={'#0089D6'} />
                    : null
                  }

                  {
                    props.data.azureUnmatchedVmCount > 0 && props.data.azureOneYearSum > 0 ? 
                    <p className="bar-warning"><img src={yellowWarningIcon} width="17" height="17" /> 1 year pricing is unavailable for {props.data.azureUnmatchedVmCount} Vms for the AWS service provider.</p>
                      : null
                  }

                  {
                    props.data.azureUnmatchedVmCount > 0 && props.data.azureOneYearSum === 0 ? 
                    <p className="bar-warning"><img src={yellowWarningIcon} width="17" height="17" /> No pricing Available for this vendor.</p>
                      : null
                  }
                </ColumnGraph>
              </CloudProviderRow>
              <CloudProviderLastRow className='provider-comparison-row'>
                <ProviderNameWrapper className='provider-name-wrap'>
                  <ProviderName>Google</ProviderName>
                </ProviderNameWrapper>
                <Column className='provider-price-wrap'>
                  <PriceStyle>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(Math.trunc(props.data.googleOneYearSum))}</PriceStyle>
                </Column>
                <ColumnGraph className='provider-bar-wrap'>
                  {
                    props.data.googleOneYearSum > 0 ?
                    <SvgBarGraph width={GetWidthPctg(props.data.googleOneYearSum)} color={'#34A853'} />
                    : null
                  }
                 
                  {
                    props.data.googleUnmatchedVmCount > 0 && props.data.googleOneYearSum > 0 ? 
                    <p className="bar-warning"><img src={yellowWarningIcon} /> 1 year pricing is unavailable for {props.data.googleUnmatchedVmCount} Vms for the AWS service provider.</p>
                      : null
                  }

                  {
                    props.data.googleUnmatchedVmCount > 0 && props.data.googleOneYearSum === 0 ? 
                    <p className="bar-warning"><img src={yellowWarningIcon} /> No pricing Available for this vendor.</p>
                      : null
                  }
                </ColumnGraph>
              </CloudProviderLastRow>
            </WrapperTile>)
        }
      </> 
      );
};

export default VmPricingComparisonChart;